// Reading one agent_captures row for display.
//
// This is the object-shaped counterpart to utils/capturedFields.ts. They look
// like the same job and are not, which is exactly why this file exists rather
// than a flag on that one:
//
//   contacts.customFields  is a raw JSON **string** off a LONGTEXT column
//   captures.fields        is a nested **object**, via JsonObjectMapConverter
//
// capturedFieldList(contact) reads `contact.customFields`, a key a Capture does
// not have, so pointing it at a capture yields zero fields and no error — the
// silent kind of wrong this codebase has shipped four times. labelFor and
// formatCapturedValue are reused below because they are pure string functions
// with no opinion about the container; nothing else from that module is.
//
// Nothing here enumerates field keys. The live wellness agent sends
// primary_wellness_goal / expected_checkin_month / companion; a salon's agent
// will share none of them. A key list was hardcoded once in this repo and had to
// be removed.
import { labelFor, formatCapturedValue } from 'utils/capturedFields';
import type { Capture, CaptureType, CaptureSyncStatus } from 'api/services/captures.service';

export type CaptureField = { key: string; label: string; value: string };

/**
 * The payload as an object, whatever arrived.
 *
 * The wire contract is an object and a test on the server pins it, so that is
 * the path that matters. A string is parsed anyway rather than dropped: the
 * lesson from api/unwrap.ts is that a reader which knows one shape fails
 * silently — it renders an empty panel over a row that has fifteen answers in
 * it. This is a fallback for a contract change, not permission to hand this
 * function a contact.
 *
 * Never throws. Arrays and scalars have no field names, so they are no fields.
 */
export function captureFields(capture: any): Record<string, unknown> {
  const raw = capture?.fields;
  if (raw === null || raw === undefined) return {};
  if (typeof raw === 'object') {
    return Array.isArray(raw) ? {} : (raw as Record<string, unknown>);
  }
  if (typeof raw !== 'string') return {};
  const trimmed = raw.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * The payload split into what the caller answered and what they did not.
 *
 * The backend keeps a key that arrived as "" ON PURPOSE: "the tool fired and
 * sent an empty expected_checkin_month" is a different fact from "the tool never
 * asked", and the two need different fixes. The contacts panel drops blanks
 * because it shows a merged summary where an empty row means nothing. Here the
 * row IS the record of one tool invocation, so the blanks are evidence and
 * throwing them away would lose the distinction this table was built to create.
 *
 * They are separated rather than interleaved so the answers stay scannable: on a
 * real lead 4 of 15 keys arrive empty, and fifteen rows of which four are blank
 * reads as a broken panel. The screen renders `answered` as values and `blank`
 * as a muted list of names.
 *
 * Both halves are sorted by key. The stored order is whatever the map
 * serialised in — not meaningful, and alphabetical is at least the same order on
 * every capture.
 */
export function captureFieldRows(capture: any): { answered: CaptureField[]; blank: CaptureField[] } {
  const parsed = captureFields(capture);
  const answered: CaptureField[] = [];
  const blank: CaptureField[] = [];

  for (const key of Object.keys(parsed).sort((a, b) => a.localeCompare(b))) {
    if (key.trim() === '') continue;
    const value = formatCapturedValue(parsed[key]);
    (value === '' ? blank : answered).push({ key, label: labelFor(key), value });
  }
  return { answered, blank };
}

/**
 * What the row is ABOUT, as a noun you can put on a control.
 *
 * Not captureTypeMeta().label, which is the BADGE's vocabulary and exists to
 * draw the distinction the screen is built on — "Rescheduled" and "Cancelled"
 * say precisely what happened to a booking, and are right on a badge. They are
 * wrong on a button: "Cancelled" as the name of a control reads as the control
 * being cancelled, not as what it opens.
 *
 * So the three booking states collapse to "Booking" here. A button should say
 * what it opens; the badge beside it already says which state the booking is in.
 */
export function captureSubject(captureType: unknown): string {
  const t = String(captureType || '').toUpperCase();
  if (t === 'LEAD') return 'Lead';
  if (t.startsWith('BOOKING')) return 'Booking';
  if (t === 'MESSAGE') return 'Message';
  return 'Capture';
}

/**
 * When the capture happened, as a Date.
 *
 * capturedAt is epoch MILLISECONDS — a number, UTC. Every LocalDateTime in cpass
 * arrives this way because AppConfig's ObjectMapper writes epoch millis and that
 * is the bean Spring MVC serialises with. `Date.parse(1755673043000)` is NaN,
 * which is how a timestamp column ends up rendering "Invalid Date"; `new
 * Date(ms)` is right. A numeric STRING is accepted too, because a proxy that
 * re-encodes JSON would produce one and a date is not worth losing to that.
 */
export function capturedAtDate(capturedAt: unknown): Date | null {
  if (typeof capturedAt === 'number' && Number.isFinite(capturedAt)) {
    const d = new Date(capturedAt);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof capturedAt === 'string' && capturedAt.trim() !== '') {
    const asNumber = Number(capturedAt);
    const d = Number.isFinite(asNumber) ? new Date(asNumber) : new Date(capturedAt);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** The timestamp in the reader's own timezone, or an em dash. */
export function formatCapturedAt(capturedAt: unknown): string {
  const d = capturedAtDate(capturedAt);
  return d ? d.toLocaleString() : '—';
}

/**
 * Who the capture is about.
 *
 * displayName is often the phone number repeated back — the tool sends
 * first_name/last_name while cpass models a single `name`, so rows exist whose
 * displayName is literally "+919024685946". Showing it as a name and then the
 * same digits again as the phone reads as a rendering bug, so a name that IS the
 * phone is treated as no name.
 */
export function captureHeadline(capture: any): string {
  return captureIdentity(capture).headline;
}

/**
 * Who this capture is, as two lines rather than one.
 *
 * `captureHeadline` has always had to choose: show the name OR the phone,
 * because showing a name and then the same digits underneath reads as a
 * rendering bug. That choice meant a capture WITH a name never showed its
 * number at all — and the number is what you dial, what you search for and what
 * the CRM de-duplicates on.
 *
 * So the decision is made once, here, and both parts are returned. `secondary`
 * is null exactly when printing it would repeat the headline: when there is no
 * phone, or when the "name" IS the phone and has already been promoted.
 */
export function captureIdentity(capture: any): { headline: string; secondary: string | null } {
  const name = String(capture?.displayName ?? '').trim();
  const phone = String(capture?.phone ?? '').trim();
  const digits = (s: string) => s.replace(/[^\d]/g, '');
  const nd = digits(name);
  const pd = digits(phone);
  // Equal digits is not enough. The same number reaches these two columns in
  // different formats — the runtime sends E.164 (+919024685946) while a name
  // written back from a national-format field is "9024685946" — so an exact
  // comparison lets the country code alone make a phone number look like a
  // name. One being the tail of the other is the same number.
  const sameNumber =
    nd.length >= 7 && pd.length >= 7 && (nd === pd || nd.endsWith(pd) || pd.endsWith(nd));
  const nameIsThePhone = name !== '' && (name === phone || sameNumber);

  if (name && !nameIsThePhone) {
    return { headline: name, secondary: phone || null };
  }
  // The headline is the number itself (or nothing at all), so there is no
  // second line to draw — repeating it would be the bug this guards against.
  return { headline: phone || 'Unnamed caller', secondary: null };
}

/**
 * The booking this capture AMENDS, if it amends one.
 *
 * On a BOOKING the externalRef is that booking's own reference. On a
 * BOOKING_RESCHEDULED or BOOKING_CANCELLED it is the reference of the booking
 * being changed — a different relationship to the same column, and the only
 * thread tying the amendment back to the row that created it. Returning it for a
 * plain BOOKING would label a booking as amending itself.
 */
export function amendedBookingRef(capture: any): string | null {
  const type = capture?.captureType;
  if (type !== 'BOOKING_RESCHEDULED' && type !== 'BOOKING_CANCELLED') return null;
  const ref = String(capture?.externalRef ?? '').trim();
  return ref === '' ? null : ref;
}

/**
 * A Java constant, rendered for a person.
 *
 * labelFor was written for the BUSINESS's snake_case field keys, which arrive
 * lowercase — it upper-cases the first letter of each word and leaves the rest
 * alone. Handed a SCREAMING_SNAKE enum name it returns "BOOKING_RESCHEDULED" as
 * "BOOKING RESCHEDULED", which is the only place in the UI that shouts. Enum
 * names are lowered first so an unrecognised one reads like every other badge.
 */
function labelForEnum(raw: string): string {
  return labelFor(raw.toLowerCase());
}

/** How each kind of capture reads to somebody who did not build the pipeline. */
export type CaptureTypeMeta = { label: string; tone: string; description: string };

const TYPE_META: Record<CaptureType, CaptureTypeMeta> = {
  LEAD: {
    label: 'Lead',
    tone: 'text-indigo-700 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300',
    description: 'Someone the agent qualified and collected details from.',
  },
  BOOKING: {
    label: 'Booking',
    tone: 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-300',
    description: 'An appointment the agent took during the call.',
  },
  BOOKING_RESCHEDULED: {
    label: 'Rescheduled',
    tone: 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'A call that moved an existing appointment.',
  },
  BOOKING_CANCELLED: {
    label: 'Cancelled',
    tone: 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300',
    description: 'A call that cancelled an existing appointment.',
  },
  MESSAGE: {
    label: 'Message',
    tone: 'text-sky-700 bg-sky-100 dark:bg-sky-900/30 dark:text-sky-300',
    description: 'Something the caller asked to be passed on.',
  },
};

/**
 * The badge for a type, including one this build has never heard of.
 *
 * A new kind of capture costs a Java constant and no schema change, so the
 * server can start sending a type this bundle predates. Falling through to an
 * empty badge would hide the row's kind — the one thing the screen exists to
 * show — so an unknown type renders its own name.
 */
export function captureTypeMeta(captureType: unknown): CaptureTypeMeta {
  const known = TYPE_META[captureType as CaptureType];
  if (known) return known;
  const raw = String(captureType ?? '').trim();
  return {
    label: raw === '' ? 'Unknown' : labelForEnum(raw),
    tone: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
    description: 'A kind of capture this screen predates.',
  };
}

/** The type filter's options, derived from the same table the badges come from. */
export const CAPTURE_TYPE_OPTIONS: { value: CaptureType; label: string }[] =
  (Object.keys(TYPE_META) as CaptureType[]).map((value) => ({ value, label: TYPE_META[value].label }));

/** How each sync outcome reads. */
export type SyncMeta = { label: string; tone: string; attention: boolean };

const SYNC_META: Record<CaptureSyncStatus, SyncMeta> = {
  SYNCED: {
    label: 'In CRM',
    tone: 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-300',
    attention: false,
  },
  QUEUED: {
    label: 'Retrying',
    tone: 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300',
    attention: true,
  },
  FAILED: {
    label: 'Not in CRM',
    tone: 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300',
    attention: true,
  },
  // Wants acting on, but it is not an integration fault and must not read as
  // one: the CRM was never asked. The lead is missing a field its own agent
  // declared mandatory, so the fix is either to complete the lead or to change
  // what that agent requires — neither of which is anything to do with Zoho.
  // Amber rather than red for that reason, and `syncError` carries the field
  // names so the row says which.
  INCOMPLETE: {
    label: 'Missing required fields',
    tone: 'text-amber-800 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-200',
    attention: true,
  },
  // Deliberately not styled as a problem. Plenty of businesses run on cpass
  // alone; painting this amber would tell every one of them that something is
  // broken every time an agent does its job correctly.
  NOT_CONFIGURED: {
    label: 'No CRM connected',
    tone: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
    attention: false,
  },
  // Also not a problem, and NOT the same fact as NOT_CONFIGURED above.
  // NOT_CONFIGURED is "nobody connected a CRM"; this is "a CRM is connected and
  // this agent is deliberately set not to push to it". Both are grey because
  // neither wants chasing, but they are different answers to "why is this lead
  // not in Zoho" and collapsing them would send somebody to fix a setting that
  // is already correct.
  //
  // It reached SYNC_META late. Before it did, the fallback below rendered it
  // grey with attention:false by accident of not recognising it — the right
  // pixels for the wrong reason, and one word in the enum away from an
  // unrecognised status quietly becoming a red badge.
  LOCAL_ONLY: {
    label: 'Kept here only',
    tone: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
    attention: false,
  },
};

export function syncMeta(syncStatus: unknown): SyncMeta {
  const known = SYNC_META[syncStatus as CaptureSyncStatus];
  if (known) return known;
  const raw = String(syncStatus ?? '').trim();
  return {
    label: raw === '' ? 'Unknown' : labelForEnum(raw),
    tone: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
    // An unrecognised status must not be counted as needing attention: it would
    // put a red banner on a page whose rows are all fine.
    attention: false,
  };
}

/**
 * How many rows on this page did not reach the CRM.
 *
 * Counted over the loaded page only, and the screen says so. The API sends no
 * per-status totals, and a count captioned as if it covered everything would
 * shrink when the user paged forward.
 */
export function attentionCount(captures: Capture[]): number {
  return (captures || []).filter((c) => syncMeta(c?.syncStatus).attention).length;
}
